import { useMemo, useState } from 'react';
import { IconFlag, IconUsersGroup } from '@tabler/icons-react';
import {
  Badge,
  Button,
  InlineAlert,
  Metric,
  Panel,
  SegmentedControl,
  Tooltip,
  cn,
  PRESS,
  formatDate,
  formatMoney,
  formatNumber,
  formatPercentage,
} from 'escrowbuild-ui';

import {
  useMilestones,
  useScheduleOf,
  useT,
  type AppTranslate,
  type AppTranslationKey,
} from '@/i18n';
import {
  PROJECTS,
  findProject,
  milestonesOf,
  projectTotals,
  scheduleOf,
  type SchedulePhase,
} from '@/data/domain';

const STATUS_TONE = {
  completed: 'success',
  'in-progress': 'info',
  planned: 'neutral',
} as const;

const STATUS_LABEL: Record<SchedulePhase['status'], AppTranslationKey> = {
  completed: 'status.phase.completed',
  'in-progress': 'status.phase.inProgress',
  planned: 'status.phase.planned',
};

/**
 * The comparison table's columns, held as keys rather than as words so the
 * header row renders through `t` like every other string on the page.
 */
const COMPARISON_COLUMNS: AppTranslationKey[] = [
  'schedule.col.phase',
  'common.status',
  'schedule.col.plannedWindow',
  'schedule.col.actualWindow',
  'schedule.col.drift',
  'schedule.col.budgeted',
  'schedule.col.actual',
  'schedule.col.variance',
  'schedule.col.spend',
];

/**
 * The portfolio table's columns. `Planned` here counts phases, so it is not the
 * status label of that name — that one is a phase's own state.
 */
const PORTFOLIO_COLUMNS: AppTranslationKey[] = [
  'schedule.portfolio.col.development',
  'schedule.portfolio.col.done',
  'schedule.portfolio.col.running',
  'schedule.portfolio.col.planned',
  'schedule.portfolio.col.budgeted',
  'schedule.portfolio.col.actual',
  'schedule.portfolio.col.late',
];

/**
 * The axis is the fixture's own units: 0–100 is a 12-month span, so one unit is
 * 1/8.33 of a month. Birch Hollow's plan runs to 132, which is why the axis is
 * measured from the data rather than assumed to stop at 100.
 */
const MONTHS_PER_UNIT = 12 / 100;

/* -----------------------------------------------------------------------------
   Axis arithmetic
   -------------------------------------------------------------------------- */

/**
 * Where month 0 falls on the calendar.
 *
 * The schedule stores positions, not dates, so an axis that reads in months has
 * to be anchored to something. The milestones are the only records in this
 * project that carry BOTH — a real date and the phase they belong to — so each
 * one is mapped back to its phase's midpoint and the earliest anchor wins. The
 * result agrees with the milestone dates themselves: Lakeview's anchor puts
 * "Framing topped out" (2026-07-24) inside the framing window it belongs to.
 */
function axisAnchor(projectId: string): Date | null {
  const phases = scheduleOf(projectId);
  let earliest: Date | null = null;

  for (const milestone of milestonesOf(projectId)) {
    const phase = phases.find((entry) => entry.id === milestone.phaseId);
    if (!phase) continue;

    const midMonth = (phase.plannedStart + phase.plannedWidth / 2) * MONTHS_PER_UNIT;
    const date = new Date(`${milestone.date}T00:00:00`);
    if (Number.isNaN(date.getTime())) continue;
    date.setDate(1);
    date.setMonth(date.getMonth() - Math.round(midMonth));

    if (!earliest || date < earliest) earliest = date;
  }

  return earliest;
}

/** Whole and fractional months between an anchor and a date. */
function monthsSince(anchor: Date, iso: string): number {
  const date = new Date(`${iso}T00:00:00`);
  return (
    (date.getFullYear() - anchor.getFullYear()) * 12 +
    (date.getMonth() - anchor.getMonth()) +
    (date.getDate() - 1) / 30
  );
}

function monthAt(anchor: Date, months: number): Date {
  const date = new Date(anchor);
  date.setMonth(date.getMonth() + Math.floor(months));
  return date;
}

function monthLabel(anchor: Date, months: number): string {
  return formatDate(monthAt(anchor, months), { style: 'monthYear' });
}

/**
 * The tick labels. Month 0 is the anchor, so a reader can walk the axis without
 * converting percentages in their head — which is the whole reason the axis is
 * labelled in months rather than in the fixture's own units.
 */
function axisTicks(
  anchor: Date | null,
  axisMax: number,
  axisMonths: number,
  t: AppTranslate,
): { at: number; label: string }[] {
  const ticks: { at: number; label: string }[] = [];
  for (let month = 0; month <= axisMonths; month += 2) {
    const at = (month / MONTHS_PER_UNIT / axisMax) * 100;
    if (at > 100.5) break;
    ticks.push({ at, label: anchor ? monthLabel(anchor, month) : t('schedule.axis.month', { month }) });
  }
  return ticks;
}

/* -----------------------------------------------------------------------------
   Schedule analysis — all derived, none stored
   -------------------------------------------------------------------------- */

interface PhaseAnalysis {
  phase: SchedulePhase;
  plannedEnd: number;
  actualEnd: number | null;
  /** Axis units past the planned end. Positive is late, negative is early. */
  drift: number;
}

function analyse(phases: SchedulePhase[]): PhaseAnalysis[] {
  return phases.map((phase) => {
    const plannedEnd = phase.plannedStart + phase.plannedWidth;
    const actualEnd =
      phase.actualStart === null || phase.actualWidth === null
        ? null
        : phase.actualStart + phase.actualWidth;
    return { phase, plannedEnd, actualEnd, drift: actualEnd === null ? 0 : actualEnd - plannedEnd };
  });
}

/**
 * The busiest point of the plan, in phases running at once.
 *
 * This is the number the document's §10 works for: phases are not a sequence,
 * and a schedule view that only stacks rows hides that. Probing just past each
 * span boundary is enough — the count only changes at a boundary.
 */
function peakConcurrency(phases: SchedulePhase[]): { count: number; month: number; names: string[] } {
  let peak = { count: 0, month: 0, names: [] as string[] };

  for (const phase of phases) {
    const probe = phase.plannedStart + 0.01;
    const active = phases.filter(
      (entry) => entry.plannedStart <= probe && probe < entry.plannedStart + entry.plannedWidth,
    );
    if (active.length > peak.count) {
      peak = { count: active.length, month: Math.floor(probe * MONTHS_PER_UNIT) + 1, names: active.map((entry) => entry.name) };
    }
  }

  return peak;
}

/** Planned windows, in months since the anchor, so the table reads as dates. */
function windowLabel(anchor: Date | null, from: number, width: number): string {
  if (!anchor) return `${formatNumber(from)}–${formatNumber(from + width)}`;
  return `${monthLabel(anchor, from * MONTHS_PER_UNIT)} – ${monthLabel(anchor, (from + width) * MONTHS_PER_UNIT)}`;
}

/* -----------------------------------------------------------------------------
   The page
   -------------------------------------------------------------------------- */

export function SchedulePage() {
  const t = useT();
  const [projectId, setProjectId] = useState(() => PROJECTS[0]?.id ?? '');
  const [showMilestones, setShowMilestones] = useState(true);

  const project = findProject(projectId);
  /* The two lists the timeline, the table and the milestone panel draw, resolved
     for the active locale. `axisAnchor` above still reads the raw fixtures, which
     is where its dates come from — and a date is not a word. */
  const phases = useScheduleOf(projectId);
  const milestones = useMilestones(projectId);
  const totals = projectTotals(projectId);

  const anchor = useMemo(() => axisAnchor(projectId), [projectId]);
  const analysis = useMemo(() => analyse(phases), [phases]);

  // The axis covers the data and never shrinks below the fixture's own 12-month
  // span, so a project that fits inside the year is still drawn at year scale.
  const axisMax = useMemo(() => {
    const ends = phases.flatMap((phase) => [
      phase.plannedStart + phase.plannedWidth,
      phase.actualStart === null || phase.actualWidth === null ? 0 : phase.actualStart + phase.actualWidth,
    ]);
    return Math.max(100, Math.ceil(Math.max(...ends, 0) / 10) * 10);
  }, [phases]);

  const axisMonths = Math.ceil(axisMax * MONTHS_PER_UNIT);
  const ticks = axisTicks(anchor, axisMax, axisMonths, t);

  const completed = phases.filter((phase) => phase.status === 'completed').length;
  const late = analysis.filter((entry) => entry.drift > 0.5);
  const peak = useMemo(() => peakConcurrency(phases), [phases]);

  /** Axis units → percentage of the drawn track. */
  const at = (position: number) => (position / axisMax) * 100;

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('schedule.title')}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted">{t('schedule.subtitle')}</p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric
          label={t('schedule.metric.complete')}
          value={completed}
          format="number"
          suffix={` / ${phases.length}`}
          hint={project ? t('schedule.metric.completeHint', { name: project.name }) : undefined}
        />
        <Metric
          label={t('schedule.metric.concurrent')}
          value={peak.count}
          format="number"
          tone={peak.count > 1 ? 'info' : 'default'}
          hint={
            peak.count > 1
              ? t('schedule.metric.concurrentHint', { month: peak.month })
              : t('schedule.metric.noOverlap')
          }
        />
        <Metric
          label={t('schedule.pastPlannedEnd')}
          value={late.length}
          format="number"
          tone={late.length > 0 ? 'warning' : 'success'}
          hint={t('schedule.metric.lateHint')}
        />
        <Metric
          label={t('project.shell.variance')}
          value={totals.varianceStarted}
          format="money"
          tone={totals.varianceStarted <= 0 ? 'success' : 'danger'}
          // The delta is the share of the started budget, not the variance
          // again: a tile printing one figure as both its value and its change
          // reads as two agreeing measurements when it is a single one. The
          // whole-life figure would also flatter the project — comparing spend
          // against the budget for phases not yet ordered always reads as a
          // large underspend.
          delta={
            totals.startedBudgeted === 0
              ? 0
              : (totals.varianceStarted / totals.startedBudgeted) * 100
          }
          deltaFormat="percentage"
          trend={totals.varianceStarted <= 0 ? 'down' : 'up'}
          deltaLabel={t('project.shell.varianceBasis', {
            amount: formatMoney(totals.startedBudgeted),
          })}
        />
      </div>

      <Panel
        title={t('schedule.timeline.title')}
        description={t('schedule.timeline.description')}
        headerActions={
          <SegmentedControl
            label={t('schedule.development')}
            size="sm"
            value={projectId}
            onValueChange={setProjectId}
            options={PROJECTS.map((entry) => ({ value: entry.id, label: entry.name.replace(' Residence', '') }))}
          />
        }
        padding="lg"
      >
        {project ? (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-body">
                {t('schedule.forecast', {
                  name: project.name,
                  city: project.city,
                  date: formatDate(project.projectedSale),
                })}
              </p>
              <Button
                variant="outline"
                size="sm"
                aria-pressed={showMilestones}
                leadingIcon={<IconFlag size={14} aria-hidden="true" />}
                onClick={() => setShowMilestones((shown) => !shown)}
              >
                {showMilestones ? t('schedule.hideMilestones') : t('schedule.showMilestones')}
              </Button>
            </div>

            <Gantt
              analysis={analysis}
              milestones={milestones}
              anchor={anchor}
              ticks={ticks}
              at={at}
              showMilestones={showMilestones}
            />

            <Legend />

            {peak.count > 1 ? (
              <InlineAlert
                tone="info"
                title={t('schedule.peak.title', { count: peak.count, month: peak.month })}
                description={t('schedule.peak.description', { names: peak.names.join(' · ') })}
              />
            ) : null}

            {late.length > 0 ? (
              <InlineAlert
                tone="warning"
                title={t('schedule.late.title', { count: late.length })}
                description={`${late
                  .map((entry) =>
                    t('schedule.late.item', {
                      name: entry.phase.name,
                      months: formatNumber(entry.drift * MONTHS_PER_UNIT, { precision: 1 }),
                    }),
                  )
                  .join('; ')}.`}
              />
            ) : (
              <InlineAlert
                tone="success"
                title={t('schedule.noLate.title')}
                description={t('schedule.noLate.description')}
              />
            )}
          </div>
        ) : (
          <p className="text-sm text-muted">{t('schedule.noProject')}</p>
        )}
      </Panel>

      <Panel
        title={t('schedule.plannedVsActual.title')}
        description={t('schedule.plannedVsActual.description')}
        padding="none"
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">
              {t('schedule.table.caption', { name: project?.name ?? '' })}
            </caption>
            <thead>
              <tr className="border-b border-line">
                {COMPARISON_COLUMNS.map((heading, index) => (
                  <th
                    key={heading}
                    scope="col"
                    className={cn(
                      'px-4 py-3 font-display text-2xs font-semibold uppercase tracking-label text-muted',
                      index === 0 ? 'text-left' : 'text-right',
                    )}
                  >
                    {t(heading)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {analysis.map(({ phase, actualEnd, drift }) => {
                const variance = phase.actual - phase.budgeted;
                const spent = phase.budgeted > 0 ? (phase.actual / phase.budgeted) * 100 : 0;
                return (
                  <tr key={phase.id} className="border-b border-line-subtle last:border-b-0">
                    <th scope="row" className="px-4 py-3 text-left font-medium text-heading">
                      {phase.name}
                    </th>
                    <td className="px-4 py-3 text-right">
                      <Badge tone={STATUS_TONE[phase.status]} variant="subtle" size="sm">
                        {t(STATUS_LABEL[phase.status])}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right text-body">
                      {windowLabel(anchor, phase.plannedStart, phase.plannedWidth)}
                    </td>
                    <td className="px-4 py-3 text-right text-body">
                      {phase.actualStart === null || phase.actualWidth === null
                        ? t('schedule.notStarted')
                        : windowLabel(anchor, phase.actualStart, phase.actualWidth)}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 text-right',
                        drift > 0.5 ? 'text-warning' : drift < -0.5 ? 'text-success' : 'text-muted',
                      )}
                    >
                      {actualEnd === null
                        ? '—'
                        : t('schedule.drift.value', {
                            value: formatNumber(drift * MONTHS_PER_UNIT, { precision: 1 }),
                          })}
                    </td>
                    <td className="px-4 py-3 text-right text-body">{formatMoney(phase.budgeted)}</td>
                    <td className="px-4 py-3 text-right text-body">{formatMoney(phase.actual)}</td>
                    <td
                      className={cn(
                        'px-4 py-3 text-right',
                        variance > 0 ? 'text-danger' : 'text-success',
                      )}
                    >
                      {formatMoney(variance)}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 text-right',
                        spent > 100 ? 'text-danger' : spent > 85 ? 'text-warning' : 'text-body',
                      )}
                    >
                      {phase.budgeted > 0 ? formatPercentage(spent, { precision: 0 }) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="border-t border-line-subtle px-4 py-3 text-xs text-muted">
          {t('schedule.table.note')}
        </p>
      </Panel>

      {/* The portfolio table compares three projects across seven columns, so it
          takes the full width rather than half of a two-column row. */}
      <div className="grid gap-6">
        <Panel
          title={t('schedule.milestones.title')}
          description={t('schedule.milestones.description', {
            name: project?.name ?? t('schedule.milestones.selected'),
          })}
          padding="none"
        >
          {milestones.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted">{t('schedule.milestones.empty')}</p>
          ) : (
            <ul className="flex list-none flex-col p-0">
              {milestones.map((milestone) => {
                const phase = phases.find((entry) => entry.id === milestone.phaseId);
                return (
                  <li
                    key={milestone.id}
                    className="flex flex-wrap items-center gap-3 border-b border-line-subtle px-4 py-3 last:border-b-0"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'grid size-7 shrink-0 place-items-center rounded-xs',
                        milestone.status === 'reached'
                          ? 'bg-success-surface text-success-on'
                          : 'bg-surface-muted text-muted',
                      )}
                    >
                      <IconFlag size={14} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-heading">{milestone.title}</span>
                      <span className="block text-xs text-muted">
                        {phase?.name ?? milestone.phaseId} · {formatDate(milestone.date, { style: 'medium' })}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <Badge
                        tone={milestone.status === 'reached' ? 'success' : 'neutral'}
                        variant="subtle"
                        size="sm"
                      >
                        {milestone.status === 'reached'
                          ? t('schedule.milestone.reached')
                          : t('schedule.milestone.upcoming')}
                      </Badge>
                      <Badge tone={milestone.notified ? 'info' : 'warning'} variant="subtle" size="sm">
                        {milestone.notified
                          ? t('schedule.milestone.notified')
                          : t('schedule.milestone.notNotified')}
                      </Badge>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel
          title={t('schedule.portfolio.title')}
          description={t('schedule.portfolio.description')}
          padding="none"
        >
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">{t('schedule.portfolio.caption')}</caption>
              <thead>
                <tr className="border-b border-line">
                  {PORTFOLIO_COLUMNS.map((heading, index) => (
                    <th
                      key={heading}
                      scope="col"
                      className={cn(
                        'px-4 py-3 font-display text-2xs font-semibold uppercase tracking-label text-muted',
                        index === 0 ? 'text-left' : 'text-right',
                      )}
                    >
                      {t(heading)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PROJECTS.map((entry) => {
                  const entryPhases = scheduleOf(entry.id);
                  const entryTotals = projectTotals(entry.id);
                  const entryLate = analyse(entryPhases).filter((item) => item.drift > 0.5).length;
                  return (
                    <tr key={entry.id} className="border-b border-line-subtle last:border-b-0">
                      <th scope="row" className="px-4 py-3 text-left">
                        <button
                          type="button"
                          onClick={() => setProjectId(entry.id)}
                          aria-current={entry.id === projectId ? 'true' : undefined}
                          className={cn(
                            'rounded-xs text-left font-medium',
                            entry.id === projectId ? 'text-primary' : 'text-heading hover:text-primary',
                            PRESS,
                            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                          )}
                        >
                          {entry.name}
                        </button>
                      </th>
                      <td className="px-4 py-3 text-right text-body">
                        {formatNumber(entryPhases.filter((phase) => phase.status === 'completed').length)}
                      </td>
                      <td className="px-4 py-3 text-right text-body">
                        {formatNumber(entryPhases.filter((phase) => phase.status === 'in-progress').length)}
                      </td>
                      <td className="px-4 py-3 text-right text-body">
                        {formatNumber(entryPhases.filter((phase) => phase.status === 'planned').length)}
                      </td>
                      <td className="px-4 py-3 text-right text-body">{formatMoney(entryTotals.budgeted)}</td>
                      <td className="px-4 py-3 text-right text-body">{formatMoney(entryTotals.actual)}</td>
                      <td
                        className={cn('px-4 py-3 text-right', entryLate > 0 ? 'text-warning' : 'text-muted')}
                      >
                        {formatNumber(entryLate)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------------
   The chart
   -------------------------------------------------------------------------- */

/**
 * A Gantt, built from positioning rather than from a dependency.
 *
 * One row per phase, three bands inside it: milestone pins on top, the planned
 * span in the middle, the actual span underneath. Because each phase owns a row
 * and every row shares the axis, an overlap between two phases is visible
 * directly — which is the property the fixture is built to show.
 *
 * The bars themselves are decorative. Each row carries its own sentence and the
 * table below repeats every number, so the picture is never the only way to read
 * the schedule.
 */
function Gantt({
  analysis,
  milestones,
  anchor,
  ticks,
  at,
  showMilestones,
}: {
  analysis: PhaseAnalysis[];
  milestones: ReturnType<typeof milestonesOf>;
  anchor: Date | null;
  ticks: { at: number; label: string }[];
  at: (position: number) => number;
  showMilestones: boolean;
}) {
  const t = useT();

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-[minmax(0,13rem)_1fr] gap-3" aria-hidden="true">
        <span />
        <div className="relative h-8">
          {ticks.map((tick) => (
            <span
              key={tick.label}
              style={{ left: `${tick.at}%` }}
              className="absolute top-0 -translate-x-1/2 whitespace-nowrap text-2xs text-muted"
            >
              {tick.label}
            </span>
          ))}
        </div>
      </div>

      <ul className="flex list-none flex-col gap-1.5 p-0">
        {analysis.map(({ phase, plannedEnd, actualEnd, drift }) => {
          const phaseMilestones = milestones.filter((milestone) => milestone.phaseId === phase.id);
          const label = [
            `${phase.name}:`,
            t('schedule.gantt.planned', {
              window: windowLabel(anchor, phase.plannedStart, phase.plannedWidth),
            }),
            phase.actualStart === null || phase.actualWidth === null
              ? t('schedule.gantt.notStarted')
              : t('schedule.gantt.actual', {
                  window: windowLabel(anchor, phase.actualStart, phase.actualWidth),
                }),
            drift > 0.5
              ? t('schedule.gantt.late', {
                  months: formatNumber(drift * MONTHS_PER_UNIT, { precision: 1 }),
                })
              : null,
          ]
            .filter(Boolean)
            .join(', ');

          return (
            <li key={phase.id} className="grid grid-cols-[minmax(0,13rem)_1fr] items-center gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-heading" title={phase.name}>
                  {phase.name}
                </p>
                <p className="truncate text-2xs text-muted">
                  {t('schedule.gantt.budgeted', { amount: formatMoney(phase.budgeted) })}
                </p>
                <p className="truncate text-2xs text-muted">
                  {t('schedule.gantt.spent', { amount: formatMoney(phase.actual) })}
                </p>
              </div>

              <div className="relative h-12">
                {/*
                  The bar's description is real text, not an `aria-label` on a
                  `role="img"`. The milestone pins inside are focusable, and a
                  `role="img"` subtree is presentational — an interactive
                  descendant of it is an ARIA violation (`nested-interactive`,
                  four nodes before this change). Keeping the same sentence as
                  screen-reader-only content preserves the description and
                  leaves the pins legally keyboard reachable.
                */}
                <p className="dhd-sr-only">{label}</p>
                {ticks.map((tick) => (
                  <span
                    key={tick.at}
                    aria-hidden="true"
                    style={{ left: `${tick.at}%` }}
                    className="absolute inset-y-0 w-px bg-line-subtle"
                  />
                ))}
                {/* The fixture's own 12-month boundary, drawn heavier so "past
                    the first year" is a place on the chart and not a guess. */}
                <span
                  aria-hidden="true"
                  style={{ left: `${at(100)}%` }}
                  className="absolute inset-y-0 w-px bg-line-strong"
                />

                {showMilestones
                  ? phaseMilestones.map((milestone) =>
                      anchor ? (
                        <Tooltip
                          key={milestone.id}
                          content={`${milestone.title} · ${formatDate(milestone.date, { style: 'medium' })}`}
                          trigger={
                            <button
                              type="button"
                              aria-label={`${milestone.title}, ${formatDate(milestone.date, { style: 'medium' })}`}
                              style={{ left: `${at(monthsSince(anchor, milestone.date) / MONTHS_PER_UNIT)}%` }}
                              className={cn(
                                'absolute top-0.5 z-10 size-3 -translate-x-1/2 rotate-45 rounded-xs border',
                                milestone.status === 'reached'
                                  ? 'border-success bg-success'
                                  : 'border-line-strong bg-surface',
                                PRESS,
                                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                              )}
                            />
                          }
                        />
                      ) : null,
                    )
                  : null}

                <span
                  aria-hidden="true"
                  style={{ left: `${at(phase.plannedStart)}%`, width: `${at(phase.plannedWidth)}%` }}
                  className="absolute top-5 h-3 rounded-xs border border-line-strong bg-surface-muted"
                />

                {actualEnd !== null && phase.actualStart !== null && phase.actualWidth !== null ? (
                  <>
                    {/* Three segments, so the bar itself says where the work was
                        early, on plan, and past the planned end. */}
                    {phase.actualStart < phase.plannedStart ? (
                      <span
                        aria-hidden="true"
                        style={{
                          left: `${at(phase.actualStart)}%`,
                          width: `${at(Math.min(phase.plannedStart, actualEnd) - phase.actualStart)}%`,
                        }}
                        className="absolute top-9 h-3 rounded-xs bg-info"
                      />
                    ) : null}
                    <span
                      aria-hidden="true"
                      style={{
                        left: `${at(Math.max(phase.actualStart, phase.plannedStart))}%`,
                        width: `${at(
                          Math.max(0, Math.min(actualEnd, plannedEnd) - Math.max(phase.actualStart, phase.plannedStart)),
                        )}%`,
                      }}
                      className={cn('absolute top-9 h-3', phase.status === 'completed' ? 'bg-primary' : 'bg-accent')}
                    />
                    {drift > 0 ? (
                      <span
                        aria-hidden="true"
                        style={{
                          left: `${at(Math.max(phase.actualStart, plannedEnd))}%`,
                          width: `${at(actualEnd - Math.max(phase.actualStart, plannedEnd))}%`,
                        }}
                        className="absolute top-9 h-3 rounded-r-xs bg-warning"
                      />
                    ) : null}
                  </>
                ) : (
                  <span
                    aria-hidden="true"
                    style={{ left: `${at(phase.plannedStart)}%`, width: `${at(phase.plannedWidth)}%` }}
                    className="absolute top-9 h-3 rounded-xs border border-dashed border-line-strong"
                  />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The swatches carry the same classes as the bars they explain. */
function Legend() {
  const t = useT();

  const entries: { labelKey: AppTranslationKey; className: string }[] = [
    { labelKey: 'status.phase.planned', className: 'border border-line-strong bg-surface-muted' },
    { labelKey: 'status.phase.completed', className: 'bg-primary' },
    { labelKey: 'status.phase.inProgress', className: 'bg-accent' },
    { labelKey: 'schedule.pastPlannedEnd', className: 'bg-warning' },
    { labelKey: 'schedule.startedEarly', className: 'bg-info' },
    { labelKey: 'schedule.notStarted', className: 'border border-dashed border-line-strong' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
      <span className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
        {t('schedule.legend.title')}
      </span>
      <ul className="flex list-none flex-wrap items-center gap-x-5 gap-y-2 p-0">
        {entries.map((entry) => (
          <li key={entry.labelKey} className="flex items-center gap-2 text-xs text-body">
            <span aria-hidden="true" className={cn('h-2.5 w-6 rounded-xs', entry.className)} />
            {t(entry.labelKey)}
          </li>
        ))}
        <li className="flex items-center gap-2 text-xs text-body">
          <IconFlag size={12} aria-hidden="true" className="text-success" />
          {t('schedule.legend.milestone')}
        </li>
        <li className="flex items-center gap-2 text-xs text-body">
          <IconUsersGroup size={12} aria-hidden="true" className="text-muted" />
          {t('schedule.legend.overlap')}
        </li>
      </ul>
    </div>
  );
}
